import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import './stockrepo.css';

/**
 * The report popup shell: the dimmed overlay, the titled panel and the brief spinner
 * before the body mounts.
 *
 * It lives here rather than inside stockrepo.js because the Dashboard's "View Table"
 * opens the same Item-Wise Sales Report and has to open it in the same frame — same
 * overlay, same header, same padding. Rebuilding that frame a second time is how the two
 * screens started looking like two different reports.
 *
 * WHY IT PORTALS TO <body>
 * -----------------------
 * `position: fixed` is only relative to the viewport while NO ancestor has a transform.
 * The Dashboard opens this popup from inside `<div className="card reports-carousel-card"
 * style={{ overflow: 'hidden' }}>`, and Dashboard.css gives every `.card` a
 * `transform: translateY(-2px)` on hover. The moment the pointer sits over that card —
 * which is exactly where the reader just clicked "View Table" — the card becomes the
 * containing block for this overlay: it stops covering the viewport, re-centres itself
 * inside a 600x400 box and gets clipped by the card's `overflow: hidden`. Because the
 * transform comes and goes with the pointer (and animates over 0.2s), the popup visibly
 * jumps and cuts off rather than failing outright.
 *
 * Mounting on <body> puts the overlay above every such ancestor, so it is viewport-fixed
 * wherever it is opened from. ItemCostBreakdownScreen already does this for the same
 * reason. Every rule that styles this popup is rooted at `.graph-popup-*`, so nothing
 * about its appearance depends on where in the tree it mounts.
 */
const GraphPopup = ({ isOpen, onClose, title, children, extraClass }) => {
    const [isMounted, setIsMounted] = useState(false);

    useEffect(() => {
        if (isOpen) {
            const timer = setTimeout(() => {
                setIsMounted(true);
            }, 150);
            return () => clearTimeout(timer);
        } else {
            setIsMounted(false);
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const body = (
        <div className="graph-popup-overlay">
            <div className={`graph-popup-content ${extraClass || ''}`} onClick={(event) => event.stopPropagation()}>
                <div className="graph-popup-header">
                    <h2 className="graph-popup-title">{title}</h2>
                    <button className="graph-popup-close" onClick={onClose}>×</button>
                </div>
                <div className="graph-popup-body">
                    {!isMounted ? (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '300px' }}>
                            <div style={{
                                width: '36px',
                                height: '36px',
                                border: '4px solid #f3f3f3',
                                borderTop: '4px solid #000000',
                                borderRadius: '50%',
                                animation: 'modal-spin 0.8s linear infinite'
                            }} />
                            <style>{`
                                @keyframes modal-spin {
                                    0% { transform: rotate(0deg); }
                                    100% { transform: rotate(360deg); }
                                }
                            `}</style>
                        </div>
                    ) : children}
                </div>
            </div>
        </div>
    );

    return ReactDOM.createPortal(body, document.body);
};

export default GraphPopup;
